<script setup lang="ts">
const localePath = useLocalePath();
const props = defineProps<{
  notification: INotification;
  readSelected: (id: string) => void;
  closeMenus: () => void;
}>();
const userStore = useUserStore();
const { getCollectibleById } = useCollectiblesController();

const handleNotificationRouting = async (notification: INotification) => {
  if (isActivityRoutingType(notification.type))
    navigateTo(localePath("/user-portal/activity"));
  if (isOfferRoutingType(notification.type))
    navigateTo(localePath("/user-portal/offers"));
  if (isCollectibleRoutingType(notification.type))
    await routeToCollectible(notification);
  if (isCraftableRoutingType(notification.type))
    await routeToCraftable(notification);
};

const routeToCollectible = async (notification: INotification) => {
  if (!notification.collectibleId) return null;
  const collectible = await getCollectibleById(notification.collectibleId);
  if (!collectible) return null;
  const routeName =
    collectible.gfalId === userStore.id
      ? "user-portal/collection"
      : "collectible";
  navigateTo(localePath("/" + routeName + "/" + collectible.id));
};

const routeToCraftable = async (notification: INotification) => {
  if (notification.craftableIds && notification.craftableIds.length > 1) {
    navigateTo(localePath(`/craft/${notification.merchantId}`));
    return;
  }
  if (notification.craftableIds && notification.craftableIds.length == 1)
    navigateTo(
      localePath(
        `/craft/${notification.merchantId}/${notification.craftableIds[0]}`
      )
    );
};

const clickNotification = (notification: INotification) => {
  if (!notification.read) {
    props.readSelected(notification.id);
  }
  props.closeMenus();
};
</script>

<template>
  <div
    @click="
      handleNotificationRouting(props.notification),
        clickNotification(props.notification)
    "
    class="flex py-2 mx-4 border-b border-neutral-500 cursor-pointer">
    <div class="w-[16%] md:w-[13%] max-w-20">
      <img
        :src="getPortraitItemImage(props.notification.imageId)"
        :alt="$t(`${props.notification.translationId}.title`)"
        class="border-secondary-500 border"
        @error="
            (e) =>
              ((e.target as HTMLImageElement).src =
                getPortraitItemImage())
          " />
    </div>
    <div class="w-[80%] md:w-[83%] ml-2">
      <p class="text-neutral-300">
        <span v-if="isStreakNotificationType(props.notification.type)">{{
          $t(getNotificationTranslationId(props.notification.type), {
            count: props.notification.streak,
          })
        }}</span>
        <span
          v-else-if="
            isRampNotificationType(props.notification.type) &&
            props.notification.status
          "
          >{{
            $t(
              getRampNotificationTranslationId(
                props.notification.type,
                props.notification.status
              )
            )
          }}</span
        >
        <span v-else-if="isCraftableNotificationType(props.notification.type)">
          {{
            $t(getNotificationTranslationId(props.notification.type), {
              count: props.notification.craftableIds?.length || 0,
            }) + (props.notification.craftableIds?.length === 1 ? ":" : "")
          }}
        </span>
        <span v-else>
          {{ $t(getNotificationTranslationId(props.notification.type)) }}
        </span>
        {{ " " }}
        <span
          v-if="isMonetaryNotification(props.notification.type)"
          class="text-neutral-50 font-bold"
          >{{ roundNum(props.notification.amount.GFAL) + " GFAL" }}
        </span>
        <span
          v-else-if="
            isCraftableNotificationType(props.notification.type) &&
            props.notification.craftableIds
          "
          class="text-neutral-50 font-bold">
          {{
            props.notification.craftableIds.length == 1
              ? $t(`${props.notification.translationId}.title`) +
                " " +
                $t(`${props.notification.translationId}.subtitle`)
              : ""
          }}
        </span>
        <span
          v-else-if="!isStreakNotificationType(props.notification.type)"
          class="text-neutral-50 font-bold"
          >{{
            $t(`${props.notification.translationId}.title`) +
            " " +
            $t(`${props.notification.translationId}.subtitle`) +
            (props.notification.collectibleNumber !== null
              ? " #" + props.notification.collectibleNumber
              : "")
          }}</span
        >
      </p>
      <p class="text-neutral-400 text-xs">
        {{ formatTimePassed(props.notification.time) }}
      </p>
    </div>
    <div class="w-[4%] flex items-center">
      <div
        v-if="!props.notification.read"
        class="h-[0.6rem] w-[0.6rem] bg-primary-500 rounded-full"></div>
    </div>
  </div>
</template>
